import { useCallback, useEffect, useState } from 'react'
import { useToast } from '../../../contexts/ToastContext'
import useGlobalCustomerStore from '../../../stores/globalCustomerStore'
import broadcastService, { BroadcastExposure } from '../../../services/broadcastService'

/**
 * One published broadcast's exposure payload, for whichever of its two views is
 * asking. Both pages read the same endpoint — splitting the response would mean
 * two queries to keep in step for no saving worth having.
 */
export const useBroadcastExposure = (id: string | undefined) => {
  const { selectedCustomer } = useGlobalCustomerStore()
  const { showError } = useToast()
  const [data, setData] = useState<BroadcastExposure | null>(null)
  const [loading, setLoading] = useState(true)
  const [locked, setLocked] = useState(false)

  const customerId = selectedCustomer?.id ? String(selectedCustomer.id) : ''

  const load = useCallback(async () => {
    if (!customerId || !id) { setLoading(false); return }
    setLoading(true)
    try {
      const res = await broadcastService.getBroadcast(customerId, id)
      setData(res.data)
      setLocked(false)
    } catch (err) {
      // 403 is the feature gate — a state to render, not a failure to toast.
      const status = (err as { status?: number })?.status
      if (status === 403) {
        setLocked(true)
      } else {
        showError('Could not load broadcast', err instanceof Error ? err.message : 'Please try again')
      }
    } finally {
      setLoading(false)
    }
  }, [customerId, id, showError])

  useEffect(() => { void load() }, [load])

  return { customerId, data, loading, locked, reload: load }
}

export default useBroadcastExposure
